import {BusinessType, createStringifier} from '../base/businesstype';

import {isValidIban} from './validator-base';

const stringifier = createStringifier('iban');

/**
 * BusinessType wrapper around ibans, exposing the iban
 * number as a formatted text.
 */
export class Iban extends BusinessType<string> {
  /**
   * Returns whether or not `value` is a Iban.
   */
  public static isIban(value: unknown): value is Iban {
    return value instanceof Iban;
  }

  /**
   * Constructs a new Iban instance.
   *
   * @param value The iban value
   * @param encryptedValue The encryptedValue, if any
   */
  public constructor(value: string, encryptedValue?: string) {
    if (typeof value !== 'string') {
      throw new Error(
        `Expected a string but got ${(typeof value === 'object' &&
          (value as object).constructor.name) ||
          typeof value}`,
      );
    } else {
      if (!isValidIban(value)) {
        throw new Error(`Cannot create Iban for invalid input ${JSON.stringify(value)}`);
      }
    }
    super(value.toUpperCase(), encryptedValue);
  }

  /**
   * Returns whether `other` is a `Iban` representing the same value.
   */
  public equals(other: unknown): boolean {
    if (!other) {
      return false;
    }

    if (other === this) {
      return true;
    }

    return Iban.isIban(other) && this.internalValue === other.internalValue;
  }

  /**
   * Exposes the value of this BusinessType as a string. The returned value will
   * always be the same.
   */
  public asString(): string {
    return this.internalValue;
  }

  public toString(): string {
    return stringifier(this);
  }
}
